import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Bed, Bath, Users, Maximize2, DollarSign, Pencil, X, Check } from 'lucide-react';
import { useUpdateApartmentDetails, useUpdateApartmentPricing } from '../../hooks/useApartments';
import type { Apartment } from '../../types/apartment';

const schema = z.object({
  bedrooms: z.number({ invalid_type_error: 'forms:required' }).int().min(0),
  bathrooms: z.number({ invalid_type_error: 'forms:required' }).int().min(0),
  maxGuests: z.number({ invalid_type_error: 'forms:required' }).int().min(1),
  sizeSquareMeters: z.union([z.number().min(0), z.nan()]).optional().transform(v => (v === undefined || Number.isNaN(v) ? undefined : v)),
  pricePerNight: z.number({ invalid_type_error: 'forms:required' }).min(0),
});

type FormData = z.infer<typeof schema>;

interface PropertyDetailsSectionProps {
  readonly apartment: Apartment;
}

export const PropertyDetailsSection = ({ apartment }: PropertyDetailsSectionProps) => {
  const { t } = useTranslation(['common', 'forms', 'errors']);
  const [isEditing, setIsEditing] = useState(false);
  const updateDetailsMutation = useUpdateApartmentDetails();
  const updatePricingMutation = useUpdateApartmentPricing();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      bedrooms: apartment.details.bedrooms,
      bathrooms: apartment.details.bathrooms,
      maxGuests: apartment.details.maxGuests,
      sizeSquareMeters: apartment.details.sizeSquareMeters,
      pricePerNight: apartment.details.pricePerNight,
    },
  });

  const handleEdit = () => {
    reset({
      bedrooms: apartment.details.bedrooms,
      bathrooms: apartment.details.bathrooms,
      maxGuests: apartment.details.maxGuests,
      sizeSquareMeters: apartment.details.sizeSquareMeters,
      pricePerNight: apartment.details.pricePerNight,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const onSubmit = async (data: FormData) => {
    try {
      await Promise.all([
        updateDetailsMutation.mutateAsync({
          id: apartment.id,
          data: {
            name: apartment.name,
            description: apartment.description,
            bedrooms: data.bedrooms,
            bathrooms: data.bathrooms,
            maxGuests: data.maxGuests,
            sizeSquareMeters: data.sizeSquareMeters,
          },
        }),
        updatePricingMutation.mutateAsync({
          id: apartment.id,
          data: { pricePerNight: data.pricePerNight },
        }),
      ]);
      setIsEditing(false);
    } catch {
      // error shown inline
    }
  };

  const isSaving = updateDetailsMutation.isPending || updatePricingMutation.isPending;
  const saveError = updateDetailsMutation.isError || updatePricingMutation.isError;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t('forms:propertyDetails')}</h2>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600"
            aria-label={t('common:edit')}
          >
            <Pencil size={16} />
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bedrooms')}</label>
              <input
                {...register('bedrooms', { valueAsNumber: true })}
                type="number" min={0}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {errors.bedrooms && <p className="text-sm text-red-600 mt-1">{errors.bedrooms.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:bathrooms')}</label>
              <input
                {...register('bathrooms', { valueAsNumber: true })}
                type="number" min={0}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {errors.bathrooms && <p className="text-sm text-red-600 mt-1">{errors.bathrooms.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:maxGuests')}</label>
              <input
                {...register('maxGuests', { valueAsNumber: true })}
                type="number" min={1}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {errors.maxGuests && <p className="text-sm text-red-600 mt-1">{errors.maxGuests.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:size')}</label>
              <input
                {...register('sizeSquareMeters', { valueAsNumber: true })}
                type="number" min={0}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {errors.sizeSquareMeters && <p className="text-sm text-red-600 mt-1">{errors.sizeSquareMeters.message}</p>}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('forms:pricePerNight')}</label>
              <input
                {...register('pricePerNight', { valueAsNumber: true })}
                type="number" min={0} step="0.01"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              {errors.pricePerNight && <p className="text-sm text-red-600 mt-1">{errors.pricePerNight.message}</p>}
            </div>
          </div>
          {saveError && (
            <p className="text-sm text-red-600">{t('errors:somethingWentWrong')}</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 py-2 px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 min-h-[44px]"
            >
              <Check size={16} />
              {isSaving ? t('common:saving') : t('common:save')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-2 py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium min-h-[44px]"
            >
              <X size={16} />
              {t('common:cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Bed size={16} className="text-primary-500" />
            <span>{apartment.details.bedrooms} {t('forms:bedrooms')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Bath size={16} className="text-primary-500" />
            <span>{apartment.details.bathrooms} {t('forms:bathrooms')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Users size={16} className="text-primary-500" />
            <span>{apartment.details.maxGuests} {t('common:guests')}</span>
          </div>
          {apartment.details.sizeSquareMeters && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Maximize2 size={16} className="text-primary-500" />
              <span>{apartment.details.sizeSquareMeters} m²</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-gray-700 col-span-2">
            <DollarSign size={16} className="text-accent-500" />
            <span className="font-bold text-lg text-gray-900">€{apartment.details.pricePerNight}</span>
            <span>{t('common:perNight')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailsSection;
